import { useQuery } from '@tanstack/react-query'
import { getNotifications } from '../api/notifications/requests'
import type { Notification } from '../types/notifications'
import { isOwnWork } from '../components/home/homeTodoPriority'

/**
 * The home To-do, the sidebar badges and the Notifications page all read the same
 * first page of the caller's notifications, so they share one request and cache.
 * The API places open assignments before pagination, so undated work is not
 * cut off behind a long list of dated reminders.
 */
export const homePrioritiesQuery = {
  queryKey: ['notifications', 'home-priorities'],
  queryFn: () => getNotifications({ page: 1, page_size: 50, priority: 'assigned' }),
  staleTime: 30_000,
}

const todayKey = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

export const notificationItems = (data: unknown): Notification[] => {
  const response = data as { data?: Notification[]; results?: Notification[] } | undefined
  return response?.data ?? response?.results ?? []
}

/** Assessments and content assigned to the signed-in person that are still open. */
export function useAssignedWork(enabled = true) {
  const query = useQuery({ ...homePrioritiesQuery, enabled })
  const today = todayKey()
  const items = notificationItems(query.data)
    .filter((item) => isOwnWork(item) && !item.is_completed)
    .sort((a, b) => (a.deadline ?? '9999').localeCompare(b.deadline ?? '9999'))
  const isOverdue = (item: Notification) => !!item.deadline && item.deadline.slice(0, 10) < today
  const assessments = items.filter((item) => item.type === 'assessment_pending')
  return {
    query,
    items,
    assessments,
    content: items.filter((item) => item.type !== 'assessment_pending'),
    overdue: items.filter(isOverdue),
    isOverdue,
  }
}
